import { LitElement, css, html, nothing } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { shopperContext, type ShopperContextType } from '../../contexts/shopper'
import { breakpointContext } from '../../contexts/breakpoint'
import { BREAKPOINT } from '../../util/breakpoints'
import { formatCurrency } from '../../util/format'
import { getCostBreakdownDescription } from '../../util/cost-breakdown'
import { type TemplateResult } from 'lit'
import { consume } from '@lit/context'
import { classMap } from 'lit/directives/class-map.js'
import { unsafeHTML } from 'lit/directives/unsafe-html.js'
import { features } from '../../util/plans'
import { withBase } from '../../util/base-path'
import { ApplicationService } from '../../services/application.service'
import { clearSelections } from '../../util/clear-selections'
import { orderDentalPlans } from '../../util/sort-plans'
import { STORAGE_KEY } from '../../util/storage-keys'
import {
  isReadOnlyFromUrl,
  shouldDisablePlanAction,
} from '../../util/read-only-actions'

interface Feature {
  key: string
  display: string | TemplateResult
  defaultValue?: string | TemplateResult
  list?: string
  compare?: string
  planCode?: string
  planName?: string
  label?: string | TemplateResult
}

@customElement('rq-dental-compare')
export class DentalCompare extends LitElement {
  @property({ type: Boolean })
  readOnly = false

  @property({ type: Boolean })
  isShopper = true

  @property({ type: Array })
  features: Feature[] = features

  @consume({ context: breakpointContext, subscribe: true })
  breakpoint!: BREAKPOINT

  @consume({ context: shopperContext, subscribe: true })
  shopperContextValue?: ShopperContextType

  static styles = [
    css`
      :host {
        all: unset;
      }
      .table-container {
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
      }
      .compare-table {
        border-collapse: collapse;
        margin-top: 1rem;
        margin-left: 0;
        margin-right: 0;
        width: 100%;
      }
      .compare-table {
        table-layout: fixed;
      }
      @media (max-width: 768px) {
        .compare-table {
          table-layout: auto;
          min-width: 600px;
        }
      }
      .compare-table th,
      .compare-table td {
        border: 1px solid var(--lh-color-neutral-200, #d1d3d4);
        padding: 0.75rem;
        text-align: left;
        color: var(--Neutral-800, #222425) !important;
        font-family: 'Blue Ridge Sans' !important;
        font-size: 18px !important;
        font-weight: 400 !important;
        line-height: 24px !important;
        vertical-align: top;
      }

      .compare-table td:first-child,
      .compare-table th:first-child {
        background-color: var(--lh-color-primary-50, #ebf9ff);
        font-weight: bold;
        width: 25%;
      }
      @media (max-width: 992px) {
        .compare-table th:not(:first-child) {
          width: 25.33% !important;
        }
      }
      .compare-table th:not(:first-child) {
        width: 33.33%;
        padding: 0;
      }
      .action-buttons {
        display: flex;
        justify-content: flex-end;
        margin-top: 1.5rem;
        gap: 1rem;
      }
      .subscript {
        font-size: 1.2rem;
        color: var(--Neutral-800, #222425);
      }
      .stars {
        color: var(--lh-color-primary-500, #00aeef);
      }
      .min-height-130 {
        min-height: 8.125rem;
      }
      .min-height-100 {
        min-height: 7rem;
      }
      .px-25px {
        padding: 0 25px;
      }
      .justify-content-start {
        display: flex;
        justify-content: flex-start;
      }
      .align-items-4 {
        display: inline-flex;
        align-items: center;
        gap: 4px;
      }
      ul {
        margin-left: -10px;
        list-style-type: disc;
      }

      lh-grid-item {
        padding: 0;
        margin: 0;
        gap: 0;
      }
      .compare-table th:not(:first-child) {
        height: 1px; /* lets child height: 100% resolve against the full row height */
        vertical-align: top;
      }

      .grid-container {
        flex: 1 1 auto;
        display: grid;
        grid-template-rows:
          minmax(6.7rem, 1fr) auto
          auto;
        gap: 1rem;
        padding: 7%;
        height: 100%;
        box-sizing: border-box;
      }

      .plan-header-row {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
      }

      @media (max-width: 768px) {
        .grid-container {
          gap: 1.6rem;
        }
        .plan-header-row {
          margin-bottom: 0.5rem;
        }
      }

      .grid-item {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        min-height: 0;
      }

      .price {
        display: flex;
        flex-direction: column;
        width: 100%;
      }

      .costs {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .price-buttons {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .sticky-buttons {
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        margin-top: auto;
        width: 100%;
      }
      .header-mobile {
        padding-left: 16px;
        padding-right: 16px;
      }
      .pt-15 {
        padding-top: 15px;
      }
      .pt-52 {
        padding-top: 3.25rem;
      }
      .height-3rem {
        height: 3.125rem;
      }
    `,
  ]

  connectedCallback(): void {
    super.connectedCallback()
  }

  disconnectedCallback(): void {
    super.disconnectedCallback()
  }

  // cost breakdown moved to shared util

  get isMobile() {
    return (
      this.breakpoint === BREAKPOINT.PHONE ||
      this.breakpoint === BREAKPOINT.TABLET
    )
  }

  private get isReadOnlyMode() {
    return isReadOnlyFromUrl()
  }

  private get hasDentalPlans() {
    const quote = this.shopperContextValue?.quoteData
    const dentalPlans = quote?.crossSalePlans || quote?.jsonPlans || []
    return dentalPlans.length > 0
  }

  private get hasVisionPlans() {
    const visionPlans =
      this.shopperContextValue?.quoteData?.individualPlans || []
    return visionPlans.length > 0
  }

  private get disableDentalActionButton() {
    return shouldDisablePlanAction({
      readOnly: this.isReadOnlyMode,
      product: 'dental',
      hasDentalPlans: this.hasDentalPlans,
      hasVisionPlans: this.hasVisionPlans,
    })
  }

  private handleSelectPlan(planId: string) {
    this.apply(planId)
  }

  private handleRecommendedPlan(planId: string) {
    const quote = this.shopperContextValue?.quoteData
    const plans = quote?.crossSalePlans || quote?.jsonPlans || ([] as any[])
    const idx = plans.findIndex((p: any) => p.planId === planId)
    if (idx < 0) return
    plans.forEach((p: any) => (p.producerRecommended = false))
    const selected = plans[idx]
    ;(selected as any).producerRecommended = true
    plans.splice(idx, 1)
    plans.unshift(selected)
    this.requestUpdate()
    // Ask parent to switch to List View tab (no route change)
    this.dispatchEvent(
      new CustomEvent('switch-to-list', { bubbles: true, composed: true })
    )
    // Trigger global update so results view re-renders and highlights.
    // Include the current quoteData in event detail to avoid clearing context.
    document.dispatchEvent(
      new CustomEvent('quote-data-updated', {
        detail: { quoteData: this.shopperContextValue?.quoteData },
      })
    )

    // Persist dental selection so email send doesn't drop the dental plan
    try {
      sessionStorage.setItem(STORAGE_KEY.DENTAL_PLAN_SELECTED, 'true')
      sessionStorage.setItem(STORAGE_KEY.DENTAL_PLAN_ID, planId)
    } catch {
      // non-blocking
    }

    // If vision plans exist, navigate to vision results so the user can continue there
    const hasVisionPlans =
      (this.shopperContextValue?.quoteData?.individualPlans || []).length > 0
    if (hasVisionPlans) {
      this.navigateToVision()
    } else {
      // Dental-only scenario - check if delivery method is email
      const isEmail =
        this.shopperContextValue?.formData?.deliveryMethod === 'e' ||
        (this.shopperContextValue?.quoteData?.quoteApplication
          ?.bolApplication &&
          (
            this.shopperContextValue.quoteData.quoteApplication
              .bolApplication as any
          )['deliveryMethod'] === 'e')

      if (isEmail) {
        // Mark vision as not selected for dental-only email
        try {
          sessionStorage.setItem(STORAGE_KEY.VISION_PLAN_SELECTED, 'false')
        } catch {
          // non-blocking
        }
        // Stay on List View - user will click "Send Selected Plans" button to navigate
        return
      }
      this.doApplyDentalOnly(planId)
    }
  }

  private navigateToVision() {
    window.history.pushState(
      {},
      '',
      withBase('vision-result') + window.location.search + window.location.hash
    )
    window.dispatchEvent(new PopStateEvent('popstate'))
  }

  private apply(planId: string) {
    // Update plan in result
    this.updatePlan(planId)
    // Persist dental selection and navigate to vision page for final submission
    try {
      sessionStorage.setItem(STORAGE_KEY.DENTAL_PLAN_SELECTED, 'true')
      sessionStorage.setItem(STORAGE_KEY.DENTAL_PLAN_ID, planId)
    } catch {
      // non-blocking
    }
    // If vision plans exist, navigate to vision; otherwise submit dental-only
    const hasVisionPlans =
      (this.shopperContextValue?.quoteData?.individualPlans || []).length > 0
    if (hasVisionPlans) {
      window.history.pushState(
        {},
        '',
        withBase('vision-result') +
          window.location.search +
          window.location.hash
      )
      window.dispatchEvent(new PopStateEvent('popstate'))
    } else {
      this.doApplyDentalOnly(planId)
    }
  }

  private updatePlan(planId: string) {
    const plans =
      this.shopperContextValue?.quoteData?.crossSalePlans ||
      this.shopperContextValue?.quoteData?.jsonPlans ||
      []
    for (const plan of plans) {
      if (plan.planId === planId) {
        // Update applicant rates if needed
        if (
          plan.applicantRate &&
          this.shopperContextValue?.quoteData?.quoteApplication?.applicants
        ) {
          plan.applicantRate.forEach((rate: any, index: number) => {
            if (
              this.shopperContextValue?.quoteData?.quoteApplication.applicants[
                index
              ]
            ) {
              this.shopperContextValue.quoteData.quoteApplication.applicants[
                index
              ].individualRate = rate.monthlyPremium
            }
          })
        }
        break
      }
    }
  }

  private doApplyDentalOnly(planId: string) {
    const quote = this.shopperContextValue?.quoteData
    const dentalPlan = (quote?.crossSalePlans || quote?.jsonPlans || []).find(
      (p: any) => p.planId === planId
    )
    if (!dentalPlan) {
      console.error('Dental plan not found:', planId)
      return
    }

    const shoppingCart = [
      {
        planId: dentalPlan.planId,
        plan: dentalPlan,
      },
    ]

    ApplicationService.submitApplication(
      quote,
      shoppingCart,
      true, // skipVision when submitting dental-only
      false, // do not skip dental
      '',
      this.shopperContextValue?.producerSession || null
    )
    // Clear dental selection after successful dental-only submission.
    clearSelections({ dental: true })
  }

  // Removed immediate submission; submission occurs from vision page after selection
  private renderPlanHeader(plan: any, index: number) {
    const isImmediateBinder =
      new URLSearchParams(window.location.search).get('view') ===
      'immediate-binder'
    const tooltipPosition = this.isMobile
      ? 'top'
      : index === 0
        ? 'right'
        : 'left'
    // Revert to using plan.description for plan name
    const planName = plan.description
    return html`<th>
      <div class="grid-container${!this.isShopper ? ' is-shopper-false' : ''}">
        <div class="grid-item plan-header-row">
          <lh-image
            src="${new URL('../../images/dental_blue.png', import.meta.url)
              .href}"
            alt="${planName}"
          ></lh-image>
          <lh-typography
            variant="p"
            size=${this.isMobile && !this.isShopper ? 'text-sm' : 'text-lg'}
            ><strong>${planName}</strong></lh-typography
          >
        </div>
        <div class="grid-item">
          <div class="costs">
            <lh-typography size="text-sm" alignment="center"
              >Cost per month</lh-typography
            >
            <div class=${classMap({ 'full-width': this.isMobile })}>
              ${isImmediateBinder
                ? html`<lh-typography type="heading" alignment="center">
                    ${formatCurrency(plan.rate?.monthlyPremium || 0)}
                  </lh-typography>`
                : html`<rq-tooltip
                    .description=${getCostBreakdownDescription(
                      plan,
                      this.shopperContextValue?.quoteData,
                      !!this.shopperContextValue?.isAgent
                    )}
                    position=${tooltipPosition}
                  >
                    <lh-typography type="heading" alignment="center">
                      ${formatCurrency(plan.rate?.monthlyPremium || 0)}
                    </lh-typography>
                  </rq-tooltip>`}
            </div>
            ${(() => {
              if (!isImmediateBinder) {
                return ''
              }
              const allPlans =
                this.shopperContextValue?.quoteData?.crossSalePlans ??
                this.shopperContextValue?.quoteData?.jsonPlans ??
                []
              // Use the longest EXTRAOVER65 text as placeholder to reserve identical height
              const placeholder = (allPlans as any[]).reduce(
                (longest: string, p: any) =>
                  (p.details?.EXTRAOVER65?.length ?? 0) > longest.length
                    ? p.details.EXTRAOVER65
                    : longest,
                ''
              )
              const text = plan.details?.EXTRAOVER65 as string | undefined
              if (text) {
                return html`<lh-typography size="text-sm" alignment="center"
                  >${unsafeHTML(
                    text.replace(/(\$[\d,]+(?:\.\d+)?)/g, '<strong>$1</strong>')
                  )}</lh-typography
                >`
              }
              if (placeholder) {
                return html`<lh-typography
                  size="text-sm"
                  alignment="center"
                  style="visibility:hidden;user-select:none;"
                  aria-hidden="true"
                  >${placeholder}</lh-typography
                >`
              }
              return ''
            })()}
          </div>
        </div>
        <div class="grid-item">
          <div class="price-buttons">
            ${this.shopperContextValue?.formData?.deliveryMethod === 'e'
              ? html`<lh-button
                  variant="filled"
                  size="md"
                  full-width
                  icon="seal-check"
                  icon-position="left"
                  ?disabled=${this.disableDentalActionButton}
                  analytics-id="ancillary-rate-quote:dental-compare:recommend-plan:button-text"
                  @click=${() => this.handleRecommendedPlan(plan.planId)}
                >
                  Recommend Plan
                </lh-button>`
              : html`<lh-button
                  variant="filled"
                  size="md"
                  icon-position="left"
                  full-width
                  ?disabled=${this.disableDentalActionButton}
                  icon="note-pencil"
                  analytics-id="ancillary-rate-quote:dental-compare:choose-plan:button-text"
                  @click=${() => this.handleSelectPlan(plan.planId)}
                >
                  Choose Plan
                </lh-button>`}
          </div>
        </div>
      </div>
    </th>`
  }

  private renderFeatureRow(feature: Feature, renderedKeys: Set<string>) {
    // Only show features marked for compare view
    if (feature.compare !== 'y') {
      return nothing
    }

    // Skip if we've already rendered this feature key
    if (renderedKeys.has(feature.key)) {
      return nothing
    }

    // Check if ANY plan has this feature key in jsonDetails
    const dentalPlans =
      this.shopperContextValue?.quoteData?.crossSalePlans ||
      this.shopperContextValue?.quoteData?.jsonPlans ||
      []
    const hasMatchingPlan = dentalPlans.some((plan: any) => {
      // If feature has a planCode, verify it matches the plan's lob
      if (feature.planCode && plan.lob !== feature.planCode) {
        return false
      }

      // Check if the feature key exists in jsonDetails
      if (plan.details?.jsonDetails) {
        try {
          const details = JSON.parse(plan.details.jsonDetails)
          return Object.prototype.hasOwnProperty.call(details, feature.key)
        } catch (e) {
          return false
        }
      }
      return false
    })

    // Only render the row if at least one plan has this feature
    if (!hasMatchingPlan) {
      return nothing
    }

    // Mark this key as rendered to prevent duplicates
    renderedKeys.add(feature.key)

    // Build ordered list using shared utility
    const orderedPlans = orderDentalPlans(dentalPlans)

    return html`<tr>
      <td>
        <lh-typography
          variant="p"
          size=${this.isMobile ? 'text-sm' : 'text-lg'}
        >
          <span class="align-items-4">
            <strong
              >${typeof feature.display === 'string'
                ? unsafeHTML(feature.display)
                : feature.display}</strong
            >
          </span>
        </lh-typography>
      </td>
      ${orderedPlans.map((plan: any) => this.renderFeatureCell(feature, plan))}
    </tr>`
  }

  private renderFeatureCell(feature: Feature, plan: any) {
    // Check if feature.planCode matches this plan's lob
    if (feature.planCode && plan.lob !== feature.planCode) {
      return html`<td>
        <lh-typography>—</lh-typography>
      </td>`
    }

    // Parse jsonDetails if it exists
    let details: Record<string, unknown> = {}
    if (plan.details?.jsonDetails) {
      try {
        details = JSON.parse(plan.details.jsonDetails) as Record<
          string,
          unknown
        >
      } catch (e) {
        console.error('Failed to parse jsonDetails:', e)
      }
    }

    // Get value from jsonDetails or fall back to direct plan property
    let value =
      (details[feature.key] as unknown) ?? plan[feature.key as keyof any]

    // Append additional service information if available
    if (typeof value === 'string' && plan.lob === 'IDTL') {
      if (details['PREVENTIVE_SERVICES'] && feature.display === 'Preventive') {
        value = value + '<br/>' + details['PREVENTIVE_SERVICES']
      } else if (details['BASIC_SERVICES'] && feature.display === 'Basic') {
        value = value + '<br/>' + details['BASIC_SERVICES']
      } else if (details['MAJOR_SERVICES'] && feature.display === 'Major') {
        value = value + '<br/>' + details['MAJOR_SERVICES']
      }
    }

    // If no value found, show "—"
    if (value === undefined || value === null) {
      return html`<td>
        <lh-typography>—</lh-typography>
      </td>`
    }

    // Display the value
    return html`<td>
      <lh-typography
        >${typeof value === 'string'
          ? unsafeHTML(value)
          : (value ?? 'Not Covered')}</lh-typography
      >
    </td>`
  }

  render() {
    // Create a Set to track rendered feature keys to prevent duplicates
    const renderedKeys = new Set<string>()

    return html`
      <lh-container
        theme="white"
        content-width="xxl"
        gutters="none"
        context-id="dental-compare"
      >
        <lh-container gutters="none" margin-bottom="md">
          <lh-typography
            >Use the
            <a
              href="https://healthnav.bcbsnc.com/?ci=COMMERCIAL&amp;network_id=15"
              target="_blank"
              >Find a Dentist tool<lh-icon
                size="md"
                name="arrow-square-out"
              ></lh-icon
            ></a>
            to search the provider network before choosing a plan.
          </lh-typography>
          <lh-typography>
            <strong>Note:</strong> Pricing may differ for individuals 65 and
            older.</lh-typography
          >
        </lh-container>
        <div class="table-container">
          <table class="compare-table">
            <tbody>
              <tr>
                <td class="align-middle">
                  <lh-typography
                    variant="p"
                    size=${this.isMobile ? 'text-sm' : 'text-lg'}
                    ><strong>Plan Name</strong></lh-typography
                  >
                </td>
                ${(() => {
                  const plans =
                    this.shopperContextValue?.quoteData?.crossSalePlans ||
                    this.shopperContextValue?.quoteData?.jsonPlans ||
                    []
                  const ordered = orderDentalPlans(plans)
                  return ordered.map((plan: any, index: number) =>
                    this.renderPlanHeader(plan, index)
                  )
                })()}
              </tr>
            </tbody>
            <tbody>
              ${this.features.map((feature) =>
                this.renderFeatureRow(feature, renderedKeys)
              )}
            </tbody>
          </table>
        </div>
      </lh-container>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rq-dental-compare': DentalCompare
  }
}
